import { HttpError } from '@magicdoor/errors';
import { Form, FormItem, useForm } from '@magicdoor/form';
import { createMutation, useLocalization } from '@magicdoor/ui';
import { Link } from '@solidjs/router';
import { createSignal, onMount, Show } from 'solid-js';
import { Input } from '~/components/auth/InputField';
import { restoreEmail, saveEmail } from '~/components/auth/emailStorage';
import { Button } from '~/components/common/Buttons';
import { Checkbox } from '~/components/common/Inputs/Checkbox';
import { LabeledSelect } from '~/components/common/Inputs/LabeledSelect';
import { IconLoader } from '~/components/ui';
import { auth } from '~/config/auth';
import { PolicyCheckbox } from './PolicyCheckbox';
import { SignInTitle } from './SignInTitle';

export const PasswordSignIn = () => {
  const { t } = useLocalization();

  const [error, setError] = createSignal<string | undefined>();
  const [companies, setCompanies] = createSignal<{ id: string; name: string }[] | undefined>();
  const signInForm = useForm();

  onMount(() => {
    restoreEmail(signInForm);
  });

  const handleSubmit = createMutation(async (store: any) => {
    const email = store.email;
    const password = store.password;
    const companyId = store.company;
    const remember = store.remember;

    try {
      const response = await auth.passwordLogin({
        email,
        password,
        companyId,
      });

      if (response.interactionCode) {
        if (remember) {
          // TODO: MagicAuth must support remember
          sessionStorage.setItem('auth_remember', 'true');
        } else {
          sessionStorage.removeItem('auth_remember');
        }

        await auth.initiateOAuthFlow(response.interactionCode);

        return;
      }
    } catch (e: any) {
      if (e instanceof HttpError) {
        if (e.getStatusCode() === 409) {
          const responseJson = e.getJson<{ companies: { id: string; name: string }[] }>();
          if (responseJson?.companies) {
            signInForm.setFieldValue('company', responseJson.companies[0].id);
            setError(undefined);
            setCompanies(responseJson.companies);
            return;
          }
        }
        if (e.getStatusCode() === 403) {
          return setError(t('Incorrect email or password'));
        } else if (e.getStatusCode() === 400) {
          return setError(t('Email and password are required'));
        }
      }
      setError(t('Unknown error'));
    }
  });

  return (
    <Form defaultForm={signInForm} onFormSubmit={handleSubmit} class="flex flex-col rounded-lg border-0 text-title-gray">
      <SignInTitle title={t('Login')} subTitle="MagicDoor" />
      <div class={`${companies()?.length ? 'hidden' : 'flex'} flex-col gap-4 text-left`}>
        <Input
          label={t('Email')}
          rules={[
            {
              required: true,
              message: t('{name} is required', { name: t('Email') }),
            },
            {
              type: 'email',
              message: t('{name} is invalid', { name: t('Email') }),
            },
          ]}
          placeholder="mail@magicdoor.com"
          formFieldName="email"
          onChange={(e) => {
            saveEmail(e.target.value);
          }}
        />

        <Input
          label={t('Password')}
          type="password"
          rules={[
            { required: true, message: t('{name} is required', { name: t('Password') }) },
            {
              length: [5, 150],
              message: t('{name} must be at least {minLength} characters', { name: t('Password'), minLength: '5' }),
            },
          ]}
          placeholder={t('Enter password')}
          formFieldName="password"
        />
        <div class="flex items-center justify-between gap-2">
          <FormItem formFieldName="remember" component={Checkbox} checkBoxClass="text-sm" label={t('Remember me')} showLabel={true} />
          <Link class="text-sm text-essential-colour hover-allowed:hover:underline" href="/auth/reset-password">
            {t('Forgot password')}?
          </Link>
        </div>
        <div class="flex gap-2">
          <PolicyCheckbox />
        </div>
      </div>
      <Show when={companies()?.length}>
        <FormItem
          label={t('Please select the company you would like to log in to')}
          labelClass="text-sm font-medium normal-case text-title-gray"
          formFieldName="company"
          component={LabeledSelect}
          options={companies()?.map((company) => ({ type: 'option', label: company.name, value: company.id })) ?? []}
          selectClass="h-14 rounded-full bg-inputbox-bg px-6"
          rules={[{ required: true, message: t('Please select company') }]}
        />
      </Show>
      <Show when={error()}>
        <div class="-mb-2 mt-2 text-center text-danger">{error()}</div>
      </Show>
      <Button type="submit" rounded="full" class="mt-6 flex justify-center">
        <Show when={handleSubmit.loading} fallback={t('Login')}>
          <IconLoader class="size-5 animate-spin" />
        </Show>
      </Button>
    </Form>
  );
};
